/**
 * RAS / AicpaSampling — attribute-sampling helper.
 *
 * Given population size, confidence level, expected deviation rate, computes
 * minimum sample size using AICPA attribute sampling formulas. Seed-able
 * random draw for reproducibility.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ChevronLeft } from 'lucide-react';
import { Button, Pill } from '@/components/primitives';

type Confidence = '90' | '95' | '99';

/** Factors for 0% expected deviation — classic AICPA attribute sample table. */
const RELIABILITY_FACTOR: Record<Confidence, number> = {
  '90': 2.3,
  '95': 3.0,
  '99': 4.6,
};

function minSampleSize(population: number, confidence: Confidence, toleranceRate: number, expectedRate: number): number {
  if (population <= 0 || toleranceRate <= 0) return 0;
  if (toleranceRate <= expectedRate) return population;
  const adjusted = toleranceRate - expectedRate;
  const factor = RELIABILITY_FACTOR[confidence];
  return Math.ceil(factor / (adjusted / 100));
}

/**
 * Mulberry32 PRNG — small, deterministic, no external dep. Acceptable for
 * attribute-sampling random draws where the seed is captured in the audit log.
 */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function (): number {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function drawSample(population: number, sampleSize: number, seed: number): number[] {
  const rng = mulberry32(seed);
  const selected = new Set<number>();
  const guard = Math.min(sampleSize, population);
  let iter = 0;
  while (selected.size < guard && iter < guard * 10) {
    const idx = Math.floor(rng() * population) + 1;
    selected.add(idx);
    iter += 1;
  }
  return [...selected].sort((a, b) => a - b);
}

export function AicpaSampling(): JSX.Element {
  const [population, setPopulation] = useState<number>(1000);
  const [confidence, setConfidence] = useState<Confidence>('95');
  const [tolerance, setTolerance] = useState<number>(5);
  const [expected, setExpected] = useState<number>(0);
  const [seed, setSeed] = useState<number>(42);
  const [drawn, setDrawn] = useState<number[]>([]);

  const sampleSize = useMemo(
    () => minSampleSize(population, confidence, tolerance, expected),
    [population, confidence, tolerance, expected],
  );

  const draw = (): void => {
    setDrawn(drawSample(population, sampleSize, seed));
  };

  return (
    <div>
      <Link
        to="/ras"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          fontSize: 12,
          color: 'var(--pcb-blue)',
          textDecoration: 'none',
          marginBottom: 'var(--sp-4)',
        }}
      >
        <ChevronLeft size={14} /> Back to RAS
      </Link>
      <header style={{ marginBottom: 'var(--sp-6)' }}>
        <h1 style={{ margin: 0 }}>AICPA attribute sampling</h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--ink-600)' }}>
          Computes minimum sample size for attribute testing. Draw is reproducible from the seed —
          capture both seed + sample in the audit report log.
        </p>
      </header>

      <div
        style={{
          display: 'grid',
          gap: 'var(--sp-5)',
          gridTemplateColumns: '1fr 1fr',
          maxWidth: 720,
          marginBottom: 'var(--sp-6)',
          padding: 'var(--sp-6)',
          background: 'var(--surface-card)',
          border: '1px solid var(--surface-border)',
          borderRadius: 'var(--r-md)',
        }}
      >
        <NumField label="Population size" value={population} onChange={setPopulation} min={1} />
        <div>
          <label htmlFor="aicpa-conf" style={labelStyle}>
            Confidence level
          </label>
          <select
            id="aicpa-conf"
            value={confidence}
            onChange={(e) => setConfidence(e.target.value as Confidence)}
            style={{ ...inputStyle, background: 'var(--surface-card)' }}
          >
            <option value="90">90%</option>
            <option value="95">95%</option>
            <option value="99">99%</option>
          </select>
        </div>
        <NumField label="Tolerable rate (%)" value={tolerance} onChange={setTolerance} step={0.1} />
        <NumField label="Expected deviation rate (%)" value={expected} onChange={setExpected} step={0.1} />
        <NumField label="Seed" value={seed} onChange={setSeed} />
      </div>

      <section
        style={{
          background: 'var(--info-bg)',
          color: 'var(--info-text)',
          border: '1px solid var(--pcb-blue)',
          borderRadius: 'var(--r-md)',
          padding: 'var(--sp-6)',
          marginBottom: 'var(--sp-6)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-5)',
        }}
      >
        <div>
          <div style={{ fontSize: 11, textTransform: 'uppercase' }}>Minimum sample size</div>
          <div style={{ fontSize: 32, fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{sampleSize}</div>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <Button variant="primary" onClick={draw} disabled={sampleSize === 0}>
            Draw random sample
          </Button>
        </div>
      </section>

      {drawn.length > 0 && (
        <section
          style={{
            padding: 'var(--sp-6)',
            background: 'var(--surface-card)',
            border: '1px solid var(--surface-border)',
            borderRadius: 'var(--r-md)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-4)', marginBottom: 'var(--sp-4)' }}>
            <h3 style={{ margin: 0 }}>Drawn indices</h3>
            <Pill variant="info">
              {drawn.length} selected · seed {seed}
            </Pill>
          </div>
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 12,
              color: 'var(--ink-800)',
              wordBreak: 'break-all',
            }}
          >
            {drawn.join(', ')}
          </div>
        </section>
      )}
    </div>
  );
}

function NumField({
  label,
  value,
  onChange,
  min,
  step,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  step?: number;
}): JSX.Element {
  const id = `num-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div>
      <label htmlFor={id} style={labelStyle}>
        {label}
      </label>
      <input
        id={id}
        type="number"
        value={value}
        min={min}
        step={step ?? 1}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        style={inputStyle}
      />
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  textTransform: 'uppercase',
  color: 'var(--ink-500)',
  marginBottom: 'var(--sp-3)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--sp-4)',
  border: '1px solid var(--ink-300)',
  borderRadius: 'var(--r-md)',
  fontSize: 13,
};
